import React from "react";
import { motion } from "framer-motion";
import {
  Award,
  ClipboardList,
  ShieldCheck,
  TrendingUp
} from "lucide-react";
import Navbar from "../../components/Navbar";

const readinessSteps = [
  {
    title: "Standards Interpretation",
    desc: "Clear understanding of accreditation requirements and applicable standards tailored to your organization.",
    icon: ClipboardList
  },
  {
    title: "System Alignment",
    desc: "Aligning policies, processes, and operations with accreditation criteria.",
    icon: ShieldCheck
  },
  {
    title: "Pre-Assessment & Mock Audits",
    desc: "Simulated audits to evaluate readiness and eliminate gaps before formal assessment.",
    icon: Award
  },
  {
    title: "Final Readiness Validation",
    desc: "Confidence-building validation ensuring successful accreditation outcomes.",
    icon: TrendingUp
  }
];

const AccreditationReadiness = () => {
  return (
    <>
    <Navbar />
    <section className="bg-gray-50 py-14 px-4 sm:px-6 lg:px-8 overflow-hidden">
      <div className="max-w-7xl mx-auto">

        {/* ================= HERO ================= */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-28">

          {/* TEXT SIDE */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, ease: "easeOut" }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-transparent bg-clip-text bg-[var(--bg-primary)] mb-4">
              Accreditation Readiness Support Services
            </h2>

            <div className="w-24 h-1 bg-[var(--bg-primary)] rounded-full mb-6"></div>

            <p className="text-gray-600 text-lg leading-relaxed mb-6">
              MBA Solutions helps organizations achieve full accreditation
              readiness through a structured, risk-based, and results-oriented
              approach aligned with international accreditation bodies.
            </p>

            <p className="text-gray-600 leading-relaxed">
              Our experts work closely with your teams to ensure systems,
              documentation, and operations meet accreditation requirements
              with confidence and clarity.
            </p>
          </motion.div>

          {/* IMAGE SIDE */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, ease: "easeOut" }}
            className="relative"
          >
            <img
              src="https://images.unsplash.com/photo-1552664730-d307ca884978"
              alt="Accreditation Consulting"
              className="rounded-3xl shadow-2xl object-cover w-full h-[420px]"
            />
            <div className="absolute inset-0 rounded-3xl bg-gradient-to-tr from-blue-500/20 to-indigo-500/20"></div>
          </motion.div>
        </div>

        {/* ================= READINESS JOURNEY ================= */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-32">
          {readinessSteps.map((step, index) => {
            const Icon = step.icon;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{
                  y: -10,
                  scale: 1.05,
                  boxShadow: "0px 25px 50px rgba(59,130,246,0.25)"
                }}
                className="bg-white rounded-3xl p-8 shadow-lg border border-gray-100 text-center relative overflow-hidden group"
              >
                <div className="w-16 h-16 mx-auto rounded-full bg-[var(--bg-primary)] text-white flex items-center justify-center mb-6 shadow-md">
                  <Icon size={28} />
                </div>

                <h3 className="text-lg font-bold text-gray-900 mb-3">
                  {step.title}
                </h3>

                <p className="text-sm text-gray-600 leading-relaxed">
                  {step.desc}
                </p>

                <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 to-purple-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </motion.div>
            );
          })}
        </div>

        {/* ================= VALUE STRIP ================= */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="relative bg-[var(--bg-primary)] rounded-3xl p-12 text-white shadow-xl"
        >
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 text-center">
            <div>
              <h4 className="text-2xl font-bold mb-2">100%</h4>
              <p className="text-sm opacity-90">Accreditation Focus</p>
            </div>
            <div>
              <h4 className="text-2xl font-bold mb-2">Zero Guesswork</h4>
              <p className="text-sm opacity-90">Clear Compliance Roadmap</p>
            </div>
            <div>
              <h4 className="text-2xl font-bold mb-2">Audit Confidence</h4>
              <p className="text-sm opacity-90">Prepared Before Assessment</p>
            </div>
          </div>
        </motion.div>

        {/* ================= SECOND VISUAL BLOCK ================= */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mt-32">

          {/* IMAGE */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <img
              src="https://images.unsplash.com/photo-1581092334651-ddf26d9a09d1"
              alt="Quality & Accreditation"
              className="rounded-3xl shadow-xl object-cover w-full h-[380px]"
            />
          </motion.div>

          {/* CONTENT */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              Why Choose MBA Solutions?
            </h3>

            <p className="text-gray-600 leading-relaxed mb-4">
              Our accreditation readiness services are designed to reduce risk,
              save time, and ensure your organization meets all technical and
              operational requirements before formal assessment.
            </p>

            <ul className="space-y-3 text-gray-700 text-sm">
              <li>✔ Experienced accreditation consultants</li>
              <li>✔ Practical and implementation-focused approach</li>
              <li>✔ End-to-end readiness support</li>
              <li>✔ Industry-recognized best practices</li>
            </ul>
          </motion.div>
        </div>

      </div>
    </section>
    </>
  );
};

export default AccreditationReadiness;
